import type { StaffingPlanInput, StaffingWorker } from '../staffing/public';
import type { OrganizationNode } from './model';
import { plannedOrganizationId } from './organizationPlan';
import { projectOrganizationStudio, studioWorkerOrganization } from './organizationStudio';

/** 개별 취소가 남은 변경의 참조·계층·종료·리더 소속 조건을 새로 깨뜨리는지 확인합니다. */
export function removeStudioChange(plan: StaffingPlanInput, kind: 'organization' | 'worker', key: string, nodes: readonly OrganizationNode[], workers: readonly StaffingWorker[]): { plan: StaffingPlanInput; error?: string } {
  if (kind === 'organization' && (plan.organizationChanges.some(row => row.key !== key && row.parent?.planItemKey === key) || plan.workerChanges.some(row => row.organization?.planItemKey === key))) {
    return { plan, error: '이 신설 조직을 참조하는 변경이 있습니다. 연결된 조직과 구성원의 이동 대상을 먼저 수정해 주세요.' };
  }
  const next = kind === 'organization' ? { ...plan, organizationChanges: plan.organizationChanges.filter(row => row.key !== key) }
    : { ...plan, workerChanges: plan.workerChanges.filter(row => row.key !== key) };
  const problems = (input: StaffingPlanInput) => {
    const projected = projectOrganizationStudio(nodes, input, workers).nodes;
    const byId = new Map(projected.map(node => [node.id, node]));
    const issues = new Map<string, string>();
    for (const node of projected) {
      const visited = new Set([node.id]);
      let current = node;
      while (current.parentId) {
        if (visited.has(current.parentId)) { issues.set(`cycle:${node.id}`, '조직의 상하 관계가 순환하게 됩니다. 연결된 조직 이동을 먼저 수정해 주세요.'); break; }
        visited.add(current.parentId);
        const parent = byId.get(current.parentId);
        if (!parent) break;
        current = parent;
      }
      if (node.pending === 'END' && (node.count > 0 || projected.some(child => child.parentId === node.id && child.pending !== 'END'))) {
        issues.set(`end:${node.id}`, `${node.name} 종료를 먼저 취소하거나, 남는 구성원·하위 조직을 다른 조직으로 이동해 주세요.`);
      }
    }
    for (const change of input.organizationChanges) {
      if (!change.managerId || change.operation === 'END') continue;
      const manager = workers.find(worker => worker.id === change.managerId);
      if (!manager) continue;
      const organizationId = change.operation === 'CREATE' ? plannedOrganizationId(change.key) : change.organizationId;
      const managerOrganizationId = studioWorkerOrganization(manager, input);
      if (!organizationId || !managerOrganizationId || !byId.has(organizationId)) continue;
      // 서버의 GetActiveOrganizationManager와 동일하게 주 소속 및 그 상위 경로를 확인합니다.
      // 조회되지 않은 구성원·조직은 추정하지 않고 제출 시 서버 검증에 맡깁니다.
      const visited = new Set<string>();
      let currentId: string | undefined = managerOrganizationId;
      let eligible = false;
      let known = true;
      while (currentId) {
        if (visited.has(currentId)) { eligible = false; break; }
        visited.add(currentId);
        const current = byId.get(currentId);
        if (!current) { known = false; break; }
        if (current.pending === 'END') { eligible = false; break; }
        if (current.id === organizationId) eligible = true;
        currentId = current.parentId;
      }
      if (known && !eligible) {
        issues.set(`manager:${change.key}`, `${byId.get(organizationId)?.name}의 리더 ${manager.name}님이 해당 조직 또는 하위 조직에서 벗어납니다. 리더 지정이나 소속 변경을 먼저 수정해 주세요.`);
      }
    }
    return issues;
  };
  const before = problems(plan);
  const introduced = [...problems(next)].find(([id]) => !before.has(id));
  return introduced ? { plan, error: introduced[1] } : { plan: next };
}
